import React, { useState, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    FormControl, RadioGroup, FormControlLabel, Radio, Button, Stack,
    TextField, Card, CardContent, Grid, Typography, Autocomplete, Checkbox, FormGroup
} from '@mui/material';
import authContext from '../../context/auth-context';
import SaleList, { SearchFormData } from '../../appStore/actions/saleList';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerLoader from '../bookingConfirmation/Utils/spinner';
import OffsetPagination from '../../comman/OffsetPagination';
import swal from 'sweetalert';
import { Button as CustomButton } from '../../components/common components/Button';
import './style.css';
import { postSearchByCriteria } from '../../contractManagement/thunks/postSearchByCriteria';
import { postAirlineSearchByCriteria } from '../../contractManagement/thunks/airlinePromotionCode';
import moment from 'moment';
import {
    mapFormToSearchPayload,
    stripEmptyPayloadKeys,
    hasSearchCriteria,
} from '../NewSale/salesPromotionSearchUtils';

const loyaltyDefaultValues = {
    promotionCode: '',
    loyaltyPoints: '',
    overrideNoProfitNoPoints: false,
};

const salesDefaultValues = {
    voidAllowed: false,
    refundAllowed: false,
    reissueAllowed: false,
    ticketingFee: '',
    voidFee: '',
    refundFee: '',
    reissueFee: '',
    bookingFee: '',
    merchantFee: '',
    pubCommissionAmount: '',
    sellPubAtCost: false,
    netLowestOfThisOrMarkup: '',
    netDefaultMarkup: '',
};

const sharedDefaultValues = {
    restriction: 'no_restriction',
    reservationDateFrom: '',
    reservationDateTo: '',
    departureRestriction: 'no_restriction',
    departureDateFrom: '',
    departureDateTo: '',
    consortiumType: '',
    consortium: '',
    agencyType: '',
    agency: null,
    agencyLocationType: '',
    agencyLocation: '',
    agentType: '',
    agent: null,
    bookingChannel: [],
    supplier: [],
    originType: '',
    origin: '',
    destinationType: '',
    destination: '',
    brandCodeType: '',
    brandCode: '',
    airlineType: '',
    airline: '',
    tripTypes: [],
    cabinTypes: [],
};

const ROUTE_BY_TYPE = {
    loyalty: '/promotions/loyalty',
    sales: '/promotions/sales',
    airline: '/promotions/airline',
};

const CREATE_LABEL_BY_TYPE = {
    loyalty: '+ Create Loyalty Promotion',
    sales: '+ Create Sales Promotion',
    airline: '+ Create Airline Promotion',
};

const resolvePromotionType = (promotionType, isLoyaltyPromotion) => {
    if (promotionType) return promotionType;
    if (isLoyaltyPromotion === true) return 'loyalty';
    if (isLoyaltyPromotion === false) return 'sales';
    return 'sales';
};

const getDefaultValues = (promotionType) => ({
    ...sharedDefaultValues,
    ...(promotionType === 'sales' ? salesDefaultValues : loyaltyDefaultValues),
    ...(promotionType === 'airline' ? {
        originType: 'include',
        destinationType: 'include',
        airlineType: 'include',
    } : {}),
});

const PromotionList = ({ promotionType: promotionTypeProp, isLoyaltyPromotion }) => {
    const promotionType = resolvePromotionType(promotionTypeProp, isLoyaltyPromotion);
    const isLoyalty = promotionType === 'loyalty';
    const isSales = promotionType === 'sales';
    const isAirline = promotionType === 'airline';
    const { orgId } = useContext(authContext);
    const history = useHistory();
    const dispatch = useDispatch();
    const SaleListData = useSelector((state) => state.SaleListDataForget.saleList);
    const [salesPromotions, setSalesPromotions] = useState(() => SaleListData);
    const [loader, setLoader] = useState(false);
    const [consortiumValues, setConsortiumValues] = useState([]);
    const [agencyOptions, setAgencyOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [limit, setLimit] = useState(100);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const lastSearchPayloadRef = useRef(null);

    const defaultValues = getDefaultValues(promotionType);
    const createRoute = `${ROUTE_BY_TYPE[promotionType]}/new`;
    const editRoutePrefix = ROUTE_BY_TYPE[promotionType];
    const createLabel = CREATE_LABEL_BY_TYPE[promotionType];
    const searchByCriteria = isAirline ? postAirlineSearchByCriteria : postSearchByCriteria;

    const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
        defaultValues,
    });

    const fetchAgencyOptions = async (query) => {
        if (!query || String(query).trim().length < 2) {
            setAgencyOptions((prev) => (prev.length ? [] : prev));
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/travelBusinesses/autoSuggest?orgId=${orgId}&adAgencyCode=${encodeURIComponent(query)}`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            const list = Array.isArray(data) ? data : data?.Items || [];
            if (data?.message) {
                setAgencyOptions((prev) => (prev.length ? [] : prev));
                return;
            }
            const opts = list.map((item) => {
                const labelKey = Object.keys(item).find((k) => k.includes('searchTravelBusinessesName') || k.includes('adTravelAgencyName'));
                const optionKey = Object.keys(item).find((k) => k.includes('adAgencyCode'));
                const travelBusinessesId = Object.keys(item).find((k) => k.includes('travelBusinessesId'));
                return {
                    label: `${item[optionKey] || ''} - ${item[labelKey] || ''}`,
                    value: item[optionKey] || '',
                    travelBusinessesId: item[travelBusinessesId] || '',
                };
            });
            setAgencyOptions(opts);
        } catch (err) {
            console.error(err);
            setAgencyOptions((prev) => (prev.length ? [] : prev));
        }
    };

    const fetchAgentOptions = async (query) => {
        const agency = watch('agency');
        const travelBusinessesId = agency?.travelBusinessesId;
        if (!travelBusinessesId) {
            setAgentOptions((prev) => (prev.length ? [] : prev));
            return;
        }
        if (!query || String(query).trim().length < 1) {
            setAgentOptions((prev) => (prev.length ? [] : prev));
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/external/autoSuggestV2?orgId=${orgId}&name=${encodeURIComponent(query)}&travelBusinessesId=${travelBusinessesId}`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            const items = data?.Items || [];
            const opts = items.map((item) => {
                const firstName = Object.keys(item).find((k) => k.includes('firstName'));
                const lastName = Object.keys(item).find((k) => k.includes('lastName'));
                const userName = Object.keys(item).find((k) => k.includes('username'));
                return {
                    label: `${item[firstName] || ''} ${item[lastName] || ''} (${item[userName] || ''})`,
                    value: item[userName] || '',
                };
            });
            setAgentOptions(opts);
        } catch (err) {
            console.error(err);
            setAgentOptions((prev) => (prev.length ? [] : prev));
        }
    };

    const radioSx = (error) => ({
        color: error ? 'red' : undefined,
        '&.Mui-checked': { color: error ? 'red' : undefined },
    });

    const renderIncludeExcludeRadios = (field, fieldState, includeLabel, excludeLabel, clearValue) => (
        <>
            <RadioGroup
                row
                value={field.value}
                onChange={(e) => {
                    const next = e.target.value;
                    field.onChange(next);
                    if (next === 'all') clearValue();
                }}
            >
                <FormControlLabel value="all" control={<Radio sx={radioSx(fieldState.error)} />} label="All" />
                <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label={includeLabel} />
                <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label={excludeLabel} />
            </RadioGroup>
            {fieldState?.error && <Typography variant="body2" color="error" sx={{ ml: 1 }}>{fieldState.error.message}</Typography>}
        </>
    );

    const today = new Date().toISOString().split('T')[0];
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

    const getConsortium = async () => {
        await fetch(`${process.env.REACT_APP_BASE_URL}/consortium/all?orgId=${orgId}`, {
            method: 'POST',
            redirect: 'follow',
        })
            .then((response) => response.json())
            .then((result) => {
                const data = result?.Items?.filter(
                    (elem) => !elem.consortiumName?.toUpperCase?.().includes('DO NOT USE')
                );
                setConsortiumValues(data || []);
            })
            .catch((error) => console.error('Error: ', error));
    };

    useEffect(() => {
        if (!isAirline) {
            getConsortium();
        }
    }, [isAirline]);

    const onSubmit = async (data) => {
        const fullPayload = mapFormToSearchPayload(data, { promotionType });
        // if (!hasSearchCriteria(fullPayload)) {
        //     swal({ text: 'Please enter at least one field', icon: 'error' });
        //     return;
        // }
        const payload = stripEmptyPayloadKeys(fullPayload);
        lastSearchPayloadRef.current = payload;
        setOffset(0);
        setLoader(true);
        try {
            const response = await searchByCriteria(payload, setLoader, { limit: 100, offset: 0 });
            if (response && typeof response === 'object' && !Array.isArray(response)) {
                const items = response?.data ?? response?.Items ?? [];
                setSalesPromotions(Array.isArray(items) ? items : []);
                setLimit(response?.limit ?? 100);
                setOffset(response?.offset ?? 0);
                setTotal(response?.total ?? 0);
                dispatch(SaleList(Array.isArray(items) ? items : []));
            } else {
                setSalesPromotions([]);
                setTotal(0);
                setOffset(0);
            }
            dispatch(SearchFormData(payload));
        } finally {
            setLoader(false);
        }
    };

    const onClear = () => {
        reset(defaultValues);
        setSalesPromotions([]);
        setTotal(0);
        setOffset(0);
        lastSearchPayloadRef.current = null;
        dispatch(SearchFormData({}));
    };

    const hasValue = (v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (typeof v === 'boolean') return true;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return true;
        return true;
    };

    const formatDisplayValue = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object' && !Array.isArray(v)) return JSON.stringify(v);
        if (Array.isArray(v)) return v.join(', ');
        if (typeof v === 'string') {
            const trimmed = v.trim();
    
            if (isoDateRegex.test(trimmed) && moment(trimmed).isValid()) {
                // 🔹 No timezone conversion
                return moment.utc(trimmed).format('DD/MM/YYYY HH:mm');
            }
    
            return trimmed;
        }
        return String(v).trim();
    };

    const LOYALTY_RIGHT_COLUMN_KEYS = ['PromotionCode', 'LoyaltyPoints', 'OverrideNoProfitNoPoints'];
    const AIRLINE_RIGHT_COLUMN_KEYS = ['PromotionCode'];
    const SALES_RIGHT_COLUMN_KEYS = [
        'TicketingFees', 'VoidFees', 'RefundFees', 'ReissueFees', 'BookingFees', 'MerchantFees',
        'AddorRemovePUBCommissionAmount', 'SellPUBAtCost', 'LowestofDefaultMarkuporthisvalue', 'AddorRemoveFromDefaultMarkup',
    ];
    const RIGHT_COLUMN_KEYS = isAirline
        ? AIRLINE_RIGHT_COLUMN_KEYS
        : isLoyalty
            ? LOYALTY_RIGHT_COLUMN_KEYS
            : isSales
                ? SALES_RIGHT_COLUMN_KEYS
                : [];
    const BOTTOM_LEFT_KEYS = isAirline
        ? ['CreatedDate', 'ModifiedDate', 'id']
        : ['CreatedDate', 'ModifiedDate', 'naID'];
    const APPLICABILITY_BOOLEAN_KEYS = ['AdAllowCancellation', 'AdAllowRefund', 'AdAllowReissue', 'SellPUBAtCost'];
    const HIDDEN_FEE_KEYS = [
        'TicketingFees', 'VoidFees', 'RefundFees', 'ReissueFees', 'BookingFees', 'MerchantFees',
        'AddorRemovePUBCommissionAmount', 'SellPUBAtCost', 'LowestofDefaultMarkuporthisvalue', 'AddorRemoveFromDefaultMarkup',
        'AdAllowCancellation', 'AdAllowRefund', 'AdAllowReissue',
    ];
    const HIDDEN_CONSORTIUM_ID_KEYS = ['ConsortiumInclusion', 'ConsortiumExclusion'];
    const HIDDEN_AIRLINE_KEYS = [
        'ConsortiumInclusion', 'ConsortiumExclusion',
        'AgencyLocationInclusion', 'AgencyLocationExclusion',
        'AgencyInclusion', 'AgencyExclusion',
        'AgentInclusion', 'AgentExclusion',
        'BrandedFareInclusion', 'BrandedFareExclusion',
        'BookingChannelInclusion', 'BookingChannelExclusion',
        'SupplierExclusion',
        'LoyaltyPoints',
        'AirlineExclusion',
        ...HIDDEN_FEE_KEYS,
    ];

    const getLeftFields = (sales) => {
        const rest = [];
        const allKeys = Object.keys(sales || {});
        for (const key of allKeys) {
            if (RIGHT_COLUMN_KEYS.includes(key) || BOTTOM_LEFT_KEYS.includes(key)) continue;
            if (!isAirline && HIDDEN_CONSORTIUM_ID_KEYS.includes(key)) continue;
            if (!isLoyalty && (key === 'OverrideNoProfitNoPoints' || key === 'overrideNoProfitNoPoints')) continue;
            if ((isLoyalty || isAirline) && HIDDEN_FEE_KEYS.includes(key)) continue;
            if (isAirline && HIDDEN_AIRLINE_KEYS.includes(key)) continue;
            const val = sales[key];
            if (isSales && APPLICABILITY_BOOLEAN_KEYS.includes(key)) {
                if (val === true) rest.push({ label: key, value: String(val) });
            } else if (hasValue(val)) {
                rest.push({ label: key, value: formatDisplayValue(val) });
            }
        }
        const bottomFields = BOTTOM_LEFT_KEYS
            .filter((k) => hasValue(sales?.[k]))
            .map((k) => ({ label: k, value: formatDisplayValue(sales[k]) }));
        return { restFields: rest, bottomFields };
    };

    const getRightFields = (sales) => {
        return RIGHT_COLUMN_KEYS
            .filter((key) => hasValue(sales?.[key]))
            .map((key) =>
                ({ label: key, value: formatDisplayValue(sales[key]) }));
    };

    const handlePageChange = async (newOffset) => {
        const payload = lastSearchPayloadRef.current;
        if (!payload) return;
        setOffset(newOffset);
        setLoader(true);
        try {
            const response = await searchByCriteria(payload, setLoader, { limit, offset: newOffset });
            if (response && typeof response === 'object' && !Array.isArray(response)) {
                const items = response?.data ?? response?.Items ?? [];
                setSalesPromotions(Array.isArray(items) ? items : []);
                setLimit(response?.limit ?? limit);
                setOffset(response?.offset ?? newOffset);
                setTotal(response?.total ?? total);
                dispatch(SaleList(Array.isArray(items) ? items : []));
            }
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <div>
                <div>
                    <div className="my-5 text-right">
                        <CustomButton buttons={[{ className: 'cm-yellow-btn', onClick: () => history.push(createRoute), label: createLabel }]} className="cm-yellow-btn" onClick={() => {}} label={createLabel} />
                    </div>
                    <Card sx={{ width: '100%', mt: 3, padding: '10px 30px' }}>
                        <CardContent>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <FormControl component="fieldset" fullWidth>
                                    <Typography gutterBottom className="sp-title">Booking / Reservation Date</Typography>
                                    <Controller
                                        name="restriction"
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                row
                                                {...field}
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    if (value === 'no_restriction') {
                                                        setValue('reservationDateFrom', '');
                                                        setValue('reservationDateTo', '');
                                                    }
                                                    field.onChange(e);
                                                }}
                                            >
                                                <FormControlLabel value="no_restriction" control={<Radio />} label="No Restriction" />
                                                <FormControlLabel value="specific_duration" control={<Radio />} label="Specific Duration" />
                                                {watch('restriction') === 'specific_duration' && (
                                                    <Stack direction="row" spacing={2} mt={2}>
                                                        <Controller
                                                            name="reservationDateFrom"
                                                            control={control}
                                                            rules={{ validate: (value) => !isAirline || watch('restriction') !== 'specific_duration' || value ? true : 'From date is required' }}
                                                            render={({ field: f, fieldState: { error } }) => (
                                                                <TextField {...f} type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth size="small" error={!!error} helperText={error?.message} />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="reservationDateTo"
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    if (isAirline && watch('restriction') === 'specific_duration' && !value) return 'To date is required';
                                                                    if (value && watch('reservationDateFrom') && value < watch('reservationDateFrom')) return 'To date must be after or same as From date';
                                                                    return true;
                                                                },
                                                            }}
                                                            render={({ field: f, fieldState: { error } }) => (
                                                                <TextField {...f} type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth inputProps={{ min: today }} size="small" error={!!error} helperText={error?.message} />
                                                            )}
                                                        />
                                                    </Stack>
                                                )}
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>

                                <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
                                    <Typography gutterBottom className="sp-title">Departure Date</Typography>
                                    <Controller
                                        name="departureRestriction"
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                row
                                                {...field}
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    if (value === 'no_restriction') {
                                                        setValue('departureDateFrom', '');
                                                        setValue('departureDateTo', '');
                                                    }
                                                    field.onChange(e);
                                                }}
                                            >
                                                <FormControlLabel value="no_restriction" control={<Radio />} label="No Restriction" />
                                                <FormControlLabel value="specific_duration" control={<Radio />} label="Specific Duration" />
                                                {watch('departureRestriction') === 'specific_duration' && (
                                                    <Stack direction="row" spacing={2} mt={2}>
                                                        <Controller
                                                            name="departureDateFrom"
                                                            control={control}
                                                            rules={{ validate: (value) => !isAirline || watch('departureRestriction') !== 'specific_duration' || value ? true : 'From date is required' }}
                                                            render={({ field: f, fieldState: { error } }) => (
                                                                <TextField {...f} type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth size="small" error={!!error} helperText={error?.message} />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="departureDateTo"
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    if (isAirline && watch('departureRestriction') === 'specific_duration' && !value) return 'To date is required';
                                                                    if (value && watch('departureDateFrom') && value < watch('departureDateFrom')) return 'To date must be after or same as From date';
                                                                    return true;
                                                                },
                                                            }}
                                                            render={({ field: f, fieldState: { error } }) => (
                                                                <TextField {...f} type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth size="small" error={!!error} helperText={error?.message} />
                                                            )}
                                                        />
                                                    </Stack>
                                                )}
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>

                                {!isAirline && (
                                <>
                                <Grid container spacing={3} mt={3}>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="consortiumType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('consortium') ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Consortium',
                                                'Exclude Consortium',
                                                () => setValue('consortium', ''),
                                            )}
                                        />
                                        <Controller
                                            name="consortium"
                                            control={control}
                                            render={({ field }) => {
                                                const selected = consortiumValues.find((opt) => opt.consortiumId === field.value) || null;
                                                const typeSet = watch('consortiumType') === 'include' || watch('consortiumType') === 'exclude';
                                                return (
                                                    <Autocomplete
                                                        options={consortiumValues}
                                                        getOptionLabel={(opt) => opt?.consortiumName || ''}
                                                        isOptionEqualToValue={(opt, val) => opt?.consortiumId === val?.consortiumId}
                                                        value={selected}
                                                        onChange={(_, val) => field.onChange(val?.consortiumId || '')}
                                                        disabled={!typeSet}
                                                        renderInput={(params) => <TextField {...params} label="Consortium" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" />}
                                                    />
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="agencyLocationType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('agencyLocation')?.trim?.() ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Agency Location',
                                                'Exclude Agency Location',
                                                () => setValue('agencyLocation', ''),
                                            )}
                                        />
                                        <Controller
                                            name="agencyLocation"
                                            control={control}
                                            rules={{ pattern: { value: /^[A-Za-z]{2}-[A-Za-z]{2}(,([A-Za-z]{2}-[A-Za-z]{2}))*$/, message: 'Invalid format. Use statecode-countrycode (e.g., ON-CA, NY-US)' } }}
                                            render={({ field, fieldState }) => {
                                                const typeSet = watch('agencyLocationType') === 'include' || watch('agencyLocationType') === 'exclude';
                                                return <TextField {...field} label="Agency Location" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" disabled={!typeSet} />;
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={3} mt={3}>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="agencyType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('agency') ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Agency',
                                                'Exclude Agency',
                                                () => {
                                                    setValue('agency', null);
                                                    setValue('agent', null);
                                                    setAgentOptions([]);
                                                },
                                            )}
                                        />
                                        <Controller
                                            name="agency"
                                            control={control}
                                            render={({ field }) => {
                                                const typeSet = watch('agencyType') === 'include' || watch('agencyType') === 'exclude';
                                                return (
                                                    <Autocomplete
                                                        options={agencyOptions}
                                                        getOptionLabel={(opt) => (opt && opt.label) || ''}
                                                        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                                                        value={field.value || null}
                                                        onChange={(_, val) => {
                                                            field.onChange(val);
                                                            if (!val) {
                                                                setValue('agent', null);
                                                                setAgentOptions([]);
                                                            }
                                                        }}
                                                        onInputChange={(_, inputValue, reason) => {
                                                            if (reason !== 'input') return;
                                                            fetchAgencyOptions(inputValue);
                                                        }}
                                                        disabled={!typeSet}
                                                        renderInput={(params) => <TextField {...params} label="Agency" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" />}
                                                    />
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="agentType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('agent') ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Agent',
                                                'Exclude Agent',
                                                () => setValue('agent', null),
                                            )}
                                        />
                                        <Controller
                                            name="agent"
                                            control={control}
                                            render={({ field }) => {
                                                const typeSet = watch('agentType') === 'include' || watch('agentType') === 'exclude';
                                                const hasAgency = !!watch('agency');
                                                const disabled = !hasAgency || !typeSet;
                                                return (
                                                    <Autocomplete
                                                        options={agentOptions}
                                                        getOptionLabel={(opt) => (opt && opt.label) || ''}
                                                        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                                                        value={field.value || null}
                                                        onChange={(_, val) => field.onChange(val)}
                                                        onInputChange={(_, inputValue, reason) => {
                                                            if (reason !== 'input') return;
                                                            fetchAgentOptions(inputValue);
                                                        }}
                                                        disabled={disabled}
                                                        renderInput={(params) => <TextField {...params} label="Agent" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" />}
                                                    />
                                                );
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                </>
                                )}

                                <Grid container spacing={3} mt={3}>
                                    <Grid item xs={12} md={6}>
                                        {!isAirline && (
                                        <Controller
                                            name="originType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('origin')?.trim?.() ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Origin',
                                                'Exclude Origin',
                                                () => setValue('origin', ''),
                                            )}
                                        />
                                        )}
                                        <Controller
                                            name="origin"
                                            control={control}
                                            rules={{ pattern: { value: /^([A-Za-z]{3}|[A-Za-z]{2}|\d)(,([A-Za-z]{3}|[A-Za-z]{2}|\d))*$/, message: 'Invalid origin codes format' } }}
                                            render={({ field, fieldState }) => {
                                                const typeSet = isAirline || watch('originType') === 'include' || watch('originType') === 'exclude';
                                                return <TextField {...field} label="Origin" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" disabled={!typeSet} />;
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {!isAirline && (
                                        <Controller
                                            name="destinationType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('destination')?.trim?.() ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Destination',
                                                'Exclude Destination',
                                                () => setValue('destination', ''),
                                            )}
                                        />
                                        )}
                                        <Controller
                                            name="destination"
                                            control={control}
                                            rules={{ pattern: { value: /^([A-Za-z]{3}|[A-Za-z]{2}|\d)(,([A-Za-z]{3}|[A-Za-z]{2}|\d))*$/, message: 'Invalid destination codes format' } }}
                                            render={({ field, fieldState }) => {
                                                const typeSet = isAirline || watch('destinationType') === 'include' || watch('destinationType') === 'exclude';
                                                return <TextField {...field} label="Destination" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" disabled={!typeSet} />;
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={3} mt={3}>
                                    {!isAirline && (
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="brandCodeType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('brandCode')?.trim?.() ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Brand Code',
                                                'Exclude Brand Code',
                                                () => setValue('brandCode', ''),
                                            )}
                                        />
                                        <Controller
                                            name="brandCode"
                                            control={control}
                                            render={({ field }) => {
                                                const typeSet = watch('brandCodeType') === 'include' || watch('brandCodeType') === 'exclude';
                                                return <TextField {...field} label="Brand Code" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" disabled={!typeSet} />;
                                            }}
                                        />
                                    </Grid>
                                    )}
                                    <Grid item xs={12} md={6}>
                                        {!isAirline && (
                                        <Controller
                                            name="airlineType"
                                            control={control}
                                            defaultValue=""
                                            rules={{ validate: (value) => (watch('airline')?.trim?.() ? (value ? true : 'Select one of the above values') : true) }}
                                            render={({ field, fieldState }) => renderIncludeExcludeRadios(
                                                field,
                                                fieldState,
                                                'Include Airline',
                                                'Exclude Airline',
                                                () => setValue('airline', ''),
                                            )}
                                        />
                                        )}
                                        <Controller
                                            name="airline"
                                            control={control}
                                            rules={{ pattern: { value: /^[A-Za-z0-9]{2}(,[A-Za-z0-9]{2})*$/, message: 'Valid airline code (e.g., AA, BA, DL)' } }}
                                            render={({ field, fieldState }) => {
                                                const typeSet = isAirline || watch('airlineType') === 'include' || watch('airlineType') === 'exclude';
                                                return <TextField {...field} label="Airline" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" disabled={!typeSet} />;
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {!isAirline && (
                                <Grid item xs={12}>
                                    <Typography gutterBottom className="sp-title mr-4 mt-4 d-flex align-items-center">Booking Channel</Typography>
                                    <Controller
                                        name="bookingChannel"
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field }) => {
                                            const handleChange = (event) => {
                                                const { value } = event.target;
                                                const idx = field.value.indexOf(value);
                                                const next = idx === -1 ? [...field.value, value] : field.value.filter((_, i) => i !== idx);
                                                field.onChange(next);
                                            };
                                            return (
                                                <FormControl component="fieldset">
                                                    <FormGroup row>
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('Web')} onChange={handleChange} value="Web" />} label="Online" />
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('CallCenter')} onChange={handleChange} value="CallCenter" />} label="Call Center" />
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('API-B2B')} onChange={handleChange} value="API-B2B" />} label="API-B2B" />
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('API-B2C')} onChange={handleChange} value="API-B2C" />} label="API-B2C" />
                                                    </FormGroup>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography gutterBottom className="sp-title mr-4 d-flex align-items-center">Supplier</Typography>
                                    <Controller
                                        name="supplier"
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field }) => {
                                            const handleChange = (event) => {
                                                const { value } = event.target;
                                                const idx = field.value.indexOf(value);
                                                const next = idx === -1 ? [...field.value, value] : field.value.filter((_, i) => i !== idx);
                                                field.onChange(next);
                                            };
                                            return (
                                                <FormControl component="fieldset">
                                                    <FormGroup row>
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('Sabre')} onChange={handleChange} value="Sabre" />} label="Sabre" />
                                                        {/* <FormControlLabel control={<Checkbox checked={field.value.includes('NDC')} onChange={handleChange} value="NDC" />} label="Duffel" /> */}
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('Sabre_NDC')} onChange={handleChange} value="Sabre_NDC" />} label="Sabre NDC" />
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('Amadeus_NDC')} onChange={handleChange} value="Amadeus_NDC" />} label="Amadeus NDC" />
                                                        {/* <FormControlLabel control={<Checkbox checked={field.value.includes('AC_NDC')} onChange={handleChange} value="AC_NDC" />} label="AC NDC" /> */}
                                                        <FormControlLabel control={<Checkbox checked={field.value.includes('HS_NDC_Aggregator')} onChange={handleChange} value="HS_NDC_Aggregator" />} label="HS NDC Aggregator" />
                                                    </FormGroup>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <Typography gutterBottom className="sp-title mr-4 mt-4 d-flex align-items-center">Trip Type</Typography>
                                    <Controller
                                        name="tripTypes"
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field }) => (
                                            <FormGroup row>
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('OW')} onChange={(e) => { const v = 'OW'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="One Way" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('RT')} onChange={(e) => { const v = 'RT'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Round Trip" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('OJ')} onChange={(e) => { const v = 'OJ'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Open Jaw" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('MC')} onChange={(e) => { const v = 'MC'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Multi City" />
                                            </FormGroup>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography gutterBottom className="sp-title mr-4 mt-4 d-flex align-items-center">Cabin Type</Typography>
                                    <Controller
                                        name="cabinTypes"
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field }) => (
                                            <FormGroup row>
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('Economy')} onChange={(e) => { const v = 'Economy'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Economy" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('PremiumEconomy')} onChange={(e) => { const v = 'PremiumEconomy'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Premium Economy" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('Business')} onChange={(e) => { const v = 'Business'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="Business" />
                                                <FormControlLabel control={<Checkbox checked={field.value.includes('First')} onChange={(e) => { const v = 'First'; field.onChange(e.target.checked ? [...field.value, v] : field.value.filter((x) => x !== v)); }} />} label="First" />
                                            </FormGroup>
                                        )}
                                    />
                                </Grid>

                                {(isLoyalty || isAirline) ? (
                                    <Grid container spacing={2} mt={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography gutterBottom className="sp-title mr-4 d-flex align-items-center">Promotion Code</Typography>
                                            <Controller
                                                name="promotionCode"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} label="Promotion Code" variant="outlined" fullWidth size="small" />
                                                )}
                                            />
                                        </Grid>
                                        {!isAirline && (
                                        <Grid item xs={12} md={6}>
                                            <Typography gutterBottom className="sp-title mr-4 d-flex align-items-center">Loyalty Points</Typography>
                                            <Controller
                                                name="loyaltyPoints"
                                                control={control}
                                                rules={{
                                                    pattern: {
                                                        value: /^$|^[0-9]+$/,
                                                        message: 'Loyalty Points must be a number',
                                                    },
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <TextField {...field} label="Loyalty Points" variant="outlined" fullWidth size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                                )}
                                            />
                                        </Grid>
                                        )}
                                        {isLoyalty && (
                                        <Grid item xs={12} md={6}>
                                            <Controller
                                                name="overrideNoProfitNoPoints"
                                                control={control}
                                                defaultValue={false}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name={field.name}
                                                                inputRef={field.ref}
                                                                checked={field.value === true}
                                                                onBlur={field.onBlur}
                                                                onChange={(e) => field.onChange(e.target.checked)}
                                                            />
                                                        }
                                                        label="Override No profit No points"
                                                        className="mt-4"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        )}
                                    </Grid>
                                ) : isSales ? (
                                    <>
                                <Grid item xs={12} md={6}>
                                    <Typography gutterBottom className="sp-title mr-4 mt-4 d-flex align-items-center">Promotional Values</Typography>
                                    <FormGroup row>
                                        <Controller name="voidAllowed" control={control} defaultValue={false} render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Void Allowed" />} />
                                        <Controller name="refundAllowed" control={control} defaultValue={false} render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Refund Allowed" />} />
                                        <Controller name="reissueAllowed" control={control} defaultValue={false} render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Reissue Allowed" />} />
                                    </FormGroup>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography gutterBottom className="sp-title mr-4 mt-4 d-flex align-items-center">Fees</Typography>
                                    <Stack direction="row" spacing={2} >
                                        <Controller
                                            name="ticketingFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number with optional +/-' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Ticketing Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Ticketing Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                        <Controller
                                            name="voidFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Void Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Void Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                        <Controller
                                            name="refundFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Refund Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Refund Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                        <Controller
                                            name="reissueFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Reissue Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Reissue Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                        <Controller
                                            name="bookingFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Booking Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Booking Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                        <Controller
                                            name="merchantFee"
                                            control={control}
                                            defaultValue=""
                                            rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                            render={({ field, fieldState }) => (
                                                <Grid container direction="column">
                                                    <Grid item><Typography className="sp-title">Merchant Fee</Typography></Grid>
                                                    <Grid item><TextField {...field} label="Merchant Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} /></Grid>
                                                </Grid>
                                            )}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography gutterBottom className="sp-title mt-4">Commission / Markup</Typography>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <Typography gutterBottom className="sp-title">Pub Fare</Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={6}>
                                                    <Typography gutterBottom className="sp-title">+/- Commission Amount</Typography>
                                                    <Controller
                                                        name="pubCommissionAmount"
                                                        control={control}
                                                        defaultValue=""
                                                        rules={{ pattern: { value: /^$|^[+-]?\$?\d+(\.\d+)?$/, message: 'Valid number with optional +/-' } }}
                                                        render={({ field, fieldState }) => <TextField {...field} label="+/- Commission Amount" variant="outlined" size="small" fullWidth error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller name="sellPubAtCost" control={control} defaultValue={false} render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Sell PUB at cost" className="mt-4" />} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography gutterBottom className="sp-title">Net Fare</Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography gutterBottom className="sp-title">Lowest of: this or markup</Typography>
                                                    <Controller
                                                        name="netLowestOfThisOrMarkup"
                                                        control={control}
                                                        defaultValue=""
                                                        rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                                        render={({ field, fieldState }) => <TextField {...field} label="Lowest of: this or markup" variant="outlined" size="small" fullWidth error={!!fieldState?.error} helperText={fieldState?.error?.message} />}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography gutterBottom className="sp-title">+/- Default Markup</Typography>
                                                    <Controller
                                                        name="netDefaultMarkup"
                                                        control={control}
                                                        defaultValue=""
                                                        rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: 'Valid number' } }}
                                                        render={({ field, fieldState }) => <TextField {...field} label="+/- Default Markup" variant="outlined" size="small" fullWidth error={!!fieldState?.error} helperText={fieldState?.error?.message} />}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                    </>
                                ) : null}

                                {Object.keys(errors).length > 0 && <Typography color="error" sx={{ mt: 2 }}>Please fill in all required fields correctly.</Typography>}
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Button type="button" variant="outlined" onClick={onClear}>Clear</Button>
                                    <Button type="submit" variant="contained">Search</Button>
                                </Stack>
                            </form>
                        </CardContent>
                    </Card>

                    {salesPromotions?.length > 0 && !loader
                        ? salesPromotions.map((sales) => {
                            const { restFields, bottomFields } = getLeftFields(sales);
                            const recordId = isAirline ? sales?.id : sales?.naID;
                            return (
                                <div key={recordId} className="salePro_content" onClick={() => history.push(`${editRoutePrefix}/${recordId}`)}>
                                    <div className="salePro_resultCard">
                                        <div className="salePro_resultLeft">
                                            <div className="salePro_resultLeftGrid">
                                                {restFields.map((field, idx) => (
                                                    <div key={idx} className="salePro_resultField">
                                                        <span className="salePro_resultLabel">{field.label}</span>: <span className="salePro_resultValue">{field.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="salePro_resultLeftBottomRow">
                                                {bottomFields.map((field, idx) => (
                                                    <div key={idx} className="salePro_resultField">
                                                        <span className="salePro_resultLabel">{field.label}</span>: <span className="salePro_resultValue">{field.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="salePro_resultRight">
                                            {getRightFields(sales).map((field, idx) => (
                                                <div key={idx} className="salePro_resultField">
                                                    <span className="salePro_resultLabel">{field.label}</span>: <span className="salePro_resultValue">{field.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                        : loader ? <div className="col-md-12 text-center mt-3"><SpinnerLoader /></div> : null}
                    {total > 0 && !loader ? (
                        <OffsetPagination
                            limit={limit}
                            offset={offset}
                            total={total}
                            onPageChange={handlePageChange}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default PromotionList;
